import { isUndefined, head, sortBy } from 'lodash'

import { eventEmitter } from '@/event-bus'
import i18n from '@/i18n/i18n'
import RetailerService from '@/services/admin/RetailerService.js'
import { build_query, errorMessage } from '@/utilities/helpers.js'

import { RETAILER_ERROR_MESSAGES } from '@/constants/errorMessages'

const { t } = i18n.global

export const namespaced = true

export const allowedFilters = [
  'id',
  'name',
  'alias',
  'address.city_id',
  'address.county_id',
  'users.firstname',
  'not_demo',
  'salesChannels.id',
  'categories.id',
  'only_parents',
  'address.city.region_id',
  'brands.id',
  'is_payable',
  'rank_id',
  'retailer_type'
]
export const allowedIncludes =
  'users,address.city,address.county,address.district,address.neighborhood,parentRetailer,subRetailers'
export const allowedSorts = '-created_at'
export const allowedAppends =
  'full_address,location,total_payable_unreleased_amount,total_payable_released_amount,total_payable_paid_amount,total_blocked_amount,is_payable_activity,checked_confirm_delivery_count,checked_signature_mismatch_count'

export const allowedFiltersForStatistics = ['by_retailer_user']

export const state = {
  retailers: null,
  retailer: {},
  users: null,
  user: {}, // remove
  salesChannels: null,
  salesChannel: {},
  meta: null,
  statistics: {},
  categories: [],
  goals: [],
  subRetailers: [],
  subRetailersMeta: null,
  brands: [],
  documents: [],
  shortSaleActivity: null,
  salesChannelStatusHistory: null,
  salesChannelStatusHistoryToggleStatus: null,
  isPayableHistory: null,
  isPayableHistoryMeta: {},
  retailerContacts: []
}

export const mutations = {
  SET_RETAILERS(state, retailers) {
    state.retailers = retailers.data
    state.meta = retailers.meta
  },
  SET_RETAILER(state, retailer) {
    retailer.ibans = sortBy(retailer.ibans, 'id').reverse()
    state.retailer = retailer
  },
  SET_RETAILER_USER(state, user) {
    state.user = user
  },
  SET_RETAILER_USERS(state, users) {
    state.users = users
  },
  SET_SALES_CHANNELS(state, salesChannels) {
    state.salesChannels = salesChannels.data
  },
  SET_SALES_CHANNEL(state, salesChannel) {
    state.salesChannel = salesChannel
  },
  SET_STATISTICS(state, statistics) {
    state.statistics = statistics
  },
  SET_CATEGORIES(state, categories) {
    state.categories = categories
  },
  SET_GOALS(state, goals) {
    state.goals = goals
  },
  SET_SUB_RETAILERS(state, subRetailers) {
    state.subRetailers = subRetailers.data
    state.subRetailersMeta = subRetailers.meta
  },
  ADD_SUB_RETAILER(state, subRetailer) {
    state.subRetailers.push(subRetailer)
  },
  SET_BRANDS(state, brands) {
    state.brands = brands
  },
  SET_DEBTS(state, debts) {
    state.debts = debts
  },
  SET_DOCUMENTS(state, documents) {
    state.documents = documents
  },
  SET_SHORT_SALE_ACTIVITY_OF_RETAILER(state, short_sale_activity) {
    state.shortSaleActivity = short_sale_activity
  },
  SET_SALES_CHANNEL_STATUS_HISTORY(state, sales_channel_status_history) {
    state.salesChannelStatusHistory = sales_channel_status_history
  },
  SET_SALES_CHANNEL_STATUS_HISTORY_TOGGLE_STATUS(state, sales_channel_status_history_toggle_status) {
    state.salesChannelStatusHistoryToggleStatus = sales_channel_status_history_toggle_status
  },
  SET_IS_PAYABLE_HISTORY(state, is_payable_history) {
    state.isPayableHistory = is_payable_history.data
    state.isPayableHistoryMeta = is_payable_history.meta
  },
  SET_IS_PAYABLE(state, is_payable) {
    state.retailer.is_payable = is_payable
  },
  SET_RANK(state, rank_id) {
    state.retailer.rank_id = rank_id
  },
  SET_RETAILER_CONTACTS(state, contacts) {
    state.retailerContacts = contacts
  }
}

export const getters = {
  getRetailerById: state => id => {
    return state.retailers ? state.retailers.find(retailer => retailer.id === id) : false
  },
  getRetailerUserById: state => id => {
    return state.retailer.users ? state.retailer.users.find(user => user.id === id) : false
  },
  getSalesChannelById: state => id => {
    return state.salesChannels ? state.salesChannels.find(salesChannel => salesChannel.id === id) : false
  },
  getAttachDetachSalesChannelStatusHistory: state => id => {
    return (
      state.salesChannelStatusHistory.filter(history => {
        return ['detached_saleschannel', 'attached_saleschannel'].includes(history.description)
      }) ?? []
    )
  },
  getActivationSalesChannelStatusHistory: state => id => {
    return (
      state.salesChannelStatusHistory.filter(history => {
        return ['deactivated_retailer', 'activated_retailer'].includes(history.description)
      }) ?? []
    )
  },
  retailerContacts: state => state.retailerContacts
}

export const actions = {
  getRetailers({ commit }, query) {
    let newQuery = build_query(
      query,
      allowedFilters,
      !isUndefined(query.include) ? query.include : allowedIncludes,
      !isUndefined(query.sort) ? query.sort : allowedSorts,
      !isUndefined(query.append) ? query.append : allowedAppends
    )
    return new Promise(resolve => {
      RetailerService.getRetailers(newQuery).then(response => {
        commit('SET_RETAILERS', response)
        resolve(response)
      })
    })
  },
  getRetailersWithoutUpdateStore({ commit }, query) {
    let newQuery = build_query(
      query,
      allowedFilters,
      !isUndefined(query.include) ? query.include : allowedIncludes,
      !isUndefined(query.sort) ? query.sort : allowedSorts,
      !isUndefined(query.append) ? query.append : allowedAppends
    )
    return new Promise(resolve => {
      RetailerService.getRetailers(newQuery).then(response => {
        resolve(response)
      })
    })
  },
  getRetailerList({ commit }, query) {
    return new Promise(resolve => {
      let newQuery = build_query(
        query,
        allowedFilters,
        !isUndefined(query.include) ? query.include : allowedIncludes,
        !isUndefined(query.sort) ? query.sort : allowedSorts,
        !isUndefined(query.append) ? query.append : allowedAppends
      )

      RetailerService.getRetailers(newQuery).then(response => {
        commit('SET_RETAILERS', response)
        resolve(response)
      })
    })
  },
  getRetailerCount({ commit }, params) {
    let newQuery = build_query(params.query, params.allowedFilters, null, null, null)
    return new Promise(resolve => {
      RetailerService.getRetailerCount(newQuery).then(response => {
        resolve(response)
      })
    })
  },
  exportRetailerPaymentList({ dispatch }) {
    return new Promise((resolve, reject) => {
      RetailerService.exportRetailerPaymentList()
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.admin.retailer.retailer_payment_export_process_started')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(error => {
          const notification = {
            type: 'danger',
            message: t('store.admin.retailer.export_retailer_payment_error')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(error)
        })
    })
  },
  exportRetailerFastPaymentList({ dispatch }) {
    return new Promise((resolve, reject) => {
      RetailerService.exportRetailerFastPaymentList()
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.admin.retailer.retailer_fast_payment_export_process_started')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(error => {
          const notification = {
            type: 'danger',
            message: t('store.admin.retailer.export_retailer_payment_error')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(error)
        })
    })
  },
  getRetailer({ commit, state, getters }, id) {
    if (id == state.retailer.id) {
      return state.retailer
    }

    let retailer = getters.getRetailerById(id)

    if (retailer) {
      commit('SET_RETAILER', retailer)
      commit('SET_RETAILER_USERS', retailer.users)
      commit('SET_RETAILER_USER', head(retailer.users))
      return retailer
    } else {
      return new Promise(resolve => {
        RetailerService.getRetailer(id).then(response => {
          commit('SET_RETAILER', response.data)
          commit('SET_RETAILER_USER', head(response.data.users))
          commit('SET_RETAILER_USERS', response.data.users)
          resolve(response.data)
        })
      })
    }
  },
  clearRetailerState({ commit }) {
    commit('SET_RETAILER', {})
  },
  getSubRetailers({ commit }, params) {
    return new Promise(resolve => {
      let newQuery = build_query(
        params.query,
        allowedFilters,
        !isUndefined(params.query.include) ? params.query.include : allowedIncludes,
        !isUndefined(params.query.sort) ? params.query.sort : allowedSorts,
        !isUndefined(params.query.append) ? params.query.append : allowedAppends
      )
      RetailerService.getSubRetailers(params.retailerId, newQuery).then(response => {
        commit('SET_SUB_RETAILERS', response)
        resolve(response)
      })
    })
  },
  getRetailerUser({ commit }, params) {
    return new Promise(resolve => {
      RetailerService.getRetailerUser(params.retailerId, params.id).then(response => {
        commit('SET_RETAILER_USER', response.data)
        resolve(response.data)
      })
    })
  },
  getSalesChannels({ dispatch, commit }, id) {
    return new Promise((resolve, reject) => {
      RetailerService.getSalesChannels(id)
        .then(response => {
          commit('SET_SALES_CHANNELS', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: RETAILER_ERROR_MESSAGES.getSalesChannelsError + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getSalesChannel({ commit, dispatch, state, getters }, params) {
    if ((params.id = state.salesChannel.id)) return state.salesChannel

    let salesChannel = getters.getSalesChannelById(params.id)

    if (salesChannel) {
      commit('SET_SALES_CHANNEL', salesChannel)
      return salesChannel
    } else {
      return new Promise((resolve, reject) => {
        RetailerService.getSalesChannel(params.retailer.id, params.id)
          .then(response => {
            commit('SET_SALES_CHANNEL', response.data)
            resolve(response)
          })
          .catch(err => {
            const notification = {
              type: 'danger',
              message: RETAILER_ERROR_MESSAGES.getSalesChannelsError + errorMessage(err)
            }
            dispatch('notification/add', notification, {
              root: true
            })
            reject(err)
          })
      })
    }
  },
  editRetailer({ dispatch, commit, state }, retailer) {
    let isCompanyTypeChanged = retailer?.is_company_type_changed ?? false

    delete retailer.is_company_type_changed

    return new Promise((resolve, reject) => {
      RetailerService.editRetailer(retailer.id, retailer)
        .then(response => {
          let notification = {}
          let otherNotification = {}

          if (response.data?.waitingApproval === true) {
            notification = {
              type: 'warning',
              message: t('store.shared.waiting_approval')
            }

            if (isCompanyTypeChanged === true && state.retailer.ibans.length !== 0) {
              otherNotification = {
                type: 'warning',
                message: t('store.admin.retailer.retailer_company_type_update_warning')
              }
            }
          } else {
            commit('SET_RETAILER', response.data)
            notification = {
              type: 'success',
              message: t('store.admin.retailer.retailer_updated')
            }

            if (isCompanyTypeChanged === true && response.data.ibans.length !== 0) {
              otherNotification = {
                type: 'warning',
                message: t('store.admin.retailer.retailer_company_type_update_warning')
              }
            }
          }

          dispatch('notification/add', notification, { root: true })
          if (otherNotification.message) {
            dispatch('notification/add', otherNotification, { root: true })
          }

          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  deleteRetailer({ dispatch, commit, state }, params) {
    return new Promise((resolve, reject) => {
      RetailerService.deleteRetailer(params.retailer.id)
        .then(response => {
          const notification = {
            type: 'warning',
            message: t('store.shared.deleted', {
              element: t('shared.common.retailer')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: t('store.shared.delete_error', {
              element: t('shared.common.retailer')
            })
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  editRetailerAddress({ dispatch, commit }, params) {
    return new Promise((resolve, reject) => {
      RetailerService.editRetailerAddress(params.retailerId, params.address)
        .then(response => {
          let notification = {}

          if (response.data?.waitingApproval === true) {
            notification = {
              type: 'warning',
              message: t('store.shared.waiting_approval')
            }
          } else {
            commit('SET_RETAILER', response.data)
            notification = {
              type: 'success',
              message: t('store.admin.retailer.retailer_updated')
            }
          }

          dispatch('notification/add', notification, {
            root: true
          })

          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  editRetailerFinance({ dispatch, commit }, params) {
    return new Promise((resolve, reject) => {
      RetailerService.editRetailerFinance(params.retailerId, params.ibans)
        .then(response => {
          let notification = {}

          if (response.data?.waitingApproval === true) {
            notification = {
              type: 'warning',
              message: t('store.shared.waiting_approval')
            }
          } else {
            commit('SET_RETAILER', response.data)
            notification = {
              type: 'success',
              message: t('store.admin.retailer.retailer_financial_information_updated')
            }
          }

          dispatch('notification/add', notification, {
            root: true
          })

          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.retailer.retailer_financial_information_update_error') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  editRetailerUser({ dispatch, commit, state }, retailers) {
    return new Promise((resolve, reject) => {
      RetailerService.editRetailerUser(state.retailer.id, state.user.id, retailers)
        .then(response => {
          let notification = {}

          if (response.data?.waitingApproval === undefined) {
            commit('SET_RETAILER_USER', response.data)
            notification = {
              type: 'success',
              message: t('store.admin.retailer.retailer_user_updated')
            }
          } else {
            notification = {
              type: 'warning',
              message: t('store.shared.waiting_approval')
            }
          }

          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  editSalesChannels({ dispatch, state }, salesChannelsId) {
    return new Promise((resolve, reject) => {
      RetailerService.editSalesChannels(state.retailer.id, salesChannelsId)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.admin.retailer.sales_channel_updated')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.retailer.sales_channel_update_error', [errorMessage(err)])
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  deleteSalesChannels({ dispatch, state }, salesChannelsId) {
    return new Promise((resolve, reject) => {
      RetailerService.deleteSalesChannels(state.retailer.id, salesChannelsId)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.admin.retailer.sales_channels_deleted')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.retailer.sales_channel_delete_error', [errorMessage(err)])
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getStatistics({ commit, dispatch }, params) {
    return new Promise((resolve, reject) => {
      let newQuery = build_query(params.query, allowedFiltersForStatistics)

      RetailerService.getStatistics(params.retailer_id, newQuery)
        .then(response => {
          commit('SET_STATISTICS', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: t('store.admin.retailer.retailer_stats_error', [errorMessage(err)])
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  getCategories({ commit, dispatch }, retailerId) {
    return new Promise((resolve, reject) => {
      RetailerService.getCategories(retailerId)
        .then(response => {
          commit('SET_CATEGORIES', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: t('store.shared.fetching_problem', {
              element: t('store.admin.retailer.retailer_categories')
            })
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  addCategory({ commit, dispatch }, category) {
    return new Promise((resolve, reject) => {
      RetailerService.attachCategory(state.retailer.id, category.id)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.shared.added', {
              element: t('shared.common.category')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          commit('SET_CATEGORIES', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: t('store.shared.adding_problem', {
              element: t('shared.common.category')
            })
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  deleteCategory({ commit, dispatch }, category) {
    return new Promise((resolve, reject) => {
      RetailerService.detachCategory(state.retailer.id, category.id)
        .then(response => {
          const notification = {
            type: 'warning',
            message: t('store.shared.deleted', {
              element: t('shared.common.category')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          commit('SET_CATEGORIES', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: t('store.shared.delete_error', {
              element: t('shared.common.category')
            })
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  exportRetailers({ dispatch }, query) {
    let newQuery = build_query(query, allowedFilters)

    return new Promise(reject => {
      RetailerService.exportRetailers(newQuery)
        .then(() => {
          const notification = {
            type: 'info',
            message: t('store.shared.exporting', {
              element: t('store.admin.retailer.retailer_list')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.shared.exporting_error', {
              element: t('store.admin.retailer.retailer_users')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  exportRetailerUsers({ dispatch }, query) {
    let newQuery = build_query(query, allowedFilters)

    return new Promise(reject => {
      RetailerService.exportRetailerUsers(newQuery)
        .then(() => {
          const notification = {
            type: 'info',
            message: t('store.shared.exporting', {
              element: t('store.admin.retailer.retailers')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.shared.exporting_error', {
              element: t('store.admin.retailer.retailers')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  exportRetailersMonthlySales({ dispatch }, query) {
    let newQuery = build_query(query, allowedFilters)

    return new Promise(reject => {
      RetailerService.exportRetailersMonthlySales(newQuery)
        .then(() => {
          const notification = {
            type: 'info',
            message: t('store.shared.exporting', {
              element: t('store.admin.retailer.orders_by_retailer_list')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.exporting_error', {
                element: t('store.admin.retailer.orders_by_retailer_list')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  updateRank({ dispatch, commit }, params) {
    return new Promise((resolve, reject) => {
      RetailerService.updateRank(params.id, params.rank_id)
        .then(response => {
          commit('SET_RANK', response.data.rank_id)
          const notification = {
            type: 'success',
            message: t('store.shared.updated', {
              element: t('store.admin.retailer.retailer_rank')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.shared.update_error', {
              element: t('store.admin.retailer.retailer_rank')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getGoals({ commit }) {
    return new Promise((resolve, reject) => {
      RetailerService.getGoals(state.retailer.id)
        .then(response => {
          commit('SET_GOALS', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: t('store.shared.fetching_problem', {
              element: t('store.admin.retailer.retailer_goals')
            })
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  addGoal({ commit, dispatch }, goal) {
    return new Promise((resolve, reject) => {
      RetailerService.attachGoal(state.retailer.id, goal.id)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.shared.added', {
              element: t('store.admin.retailer.retailer_goal')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          commit('SET_GOALS', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: t('store.shared.adding_problem', {
              element: t('store.admin.retailer.retailer_goal')
            })
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  deleteGoal({ commit, dispatch }, goal) {
    return new Promise((resolve, reject) => {
      RetailerService.detachGoal(state.retailer.id, goal.id)
        .then(response => {
          const notification = {
            type: 'warning',
            message: t('store.shared.deleted', {
              element: t('store.admin.retailer.retailer_goal')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          commit('SET_GOALS', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: t('store.shared.delete_error', {
              element: t('store.admin.retailer.retailer_goal')
            })
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  updateLocation({ commit, dispatch }, location) {
    return new Promise((resolve, reject) => {
      RetailerService.updateLocation(state.retailer.id, location)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.shared.updated', {
              element: t('store.admin.retailer.location')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: t('store.shared.update_error', {
              element: t('store.admin.retailer.location')
            })
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  createSubRetailer({ dispatch }, params) {
    return new Promise((resolve, reject) => {
      RetailerService.createSubRetailer(params.retailerId, params.subRetailer)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.shared.created', {
              element: t('store.admin.retailer.sub_retailer')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: t('store.shared.create_error', {
              element: t('store.admin.retailer.sub_retailer')
            })
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  updateSubRetailer({ dispatch }, params) {
    return new Promise((resolve, reject) => {
      RetailerService.updateSubRetailer(params.retailerId, params.parentId, params.subRetailer)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.shared.updated', {
              element: t('store.admin.retailer.sub_retailer')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: t('store.shared.update_error', {
              element: t('store.admin.retailer.sub_retailer')
            })
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  updateIsPayable({ dispatch, commit }, params) {
    return new Promise((resolve, reject) => {
      RetailerService.updateIsPayable(params.id, params.is_payable)
        .then(response => {
          commit('SET_IS_PAYABLE', response.data)
          resolve(response.data)
        })
        .catch(error => {
          const notification = {
            type: 'danger',
            message: t('store.admin.retailer.is_payable_update_error')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(error)
        })
    })
  },
  getBrands({ commit, dispatch }, retailerId) {
    return new Promise((resolve, reject) => {
      RetailerService.getBrands(retailerId)
        .then(response => {
          commit('SET_BRANDS', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: t('store.shared.fetching_problem', {
              element: t('store.admin.retailer.retailer_brands')
            })
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  attachBrand({ commit, dispatch, state }, params) {
    return new Promise((resolve, reject) => {
      RetailerService.attachBrand(state.retailer.id, params.categoryId, params.brands)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.shared.added', {
              element: t('shared.common.brand')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          commit('SET_BRANDS', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.adding_problem', {
                element: t('shared.common.brand')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  removeBrand({ commit, dispatch, state }, params) {
    return new Promise(resolve => {
      RetailerService.removeBrand(state.retailer.id, params.categoryId, params.brandId)
        .then(response => {
          commit('SET_BRANDS', response.data)
          const notification = {
            type: 'warning',
            message: t('store.shared.deleted', {
              element: t('shared.common.brand')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.delete_error', {
                element: t('shared.common.brand')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getDocuments({ commit, dispatch }, retailerId) {
    return new Promise((resolve, reject) => {
      RetailerService.getDocuments(retailerId)
        .then(response => {
          commit('SET_DOCUMENTS', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: t('store.shared.fetching_problem', {
              element: t('store.admin.retailer.retailer_documents')
            })
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  getShortSaleActivity({ commit, dispatch }, retailerId) {
    return new Promise((resolve, reject) => {
      RetailerService.getShortSaleActivity(retailerId)
        .then(response => {
          commit('SET_SHORT_SALE_ACTIVITY_OF_RETAILER', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: t('store.shared.fetching_problem', {
              element: t('store.admin.retailer.retailer_documents')
            })
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  getSalesChannelsStatusHistory({ commit, dispatch }, retailerId) {
    return new Promise((resolve, reject) => {
      RetailerService.getSalesChannelsStatusHistory(retailerId)
        .then(response => {
          commit('SET_SALES_CHANNEL_STATUS_HISTORY', response.data)
          commit('SET_SALES_CHANNEL_STATUS_HISTORY_TOGGLE_STATUS', response.toggle_status)
          resolve(response.data)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: t('store.shared.fetching_problem', {
              element: 'Satış kanalı aktivite dökümü'
            })
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  getIsPayableHistory({ commit, dispatch }, { retailer_id, perPage }) {
    return new Promise((resolve, reject) => {
      RetailerService.getIsPayableHistory(retailer_id, {
        per_page: perPage
      })
        .then(response => {
          commit('SET_IS_PAYABLE_HISTORY', response)
          resolve(response)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: t('store.shared.fetching_problem', {
              element: 'Ödeme engeli aktivite dökümü'
            })
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  uploadRetailerDocument({ commit, dispatch }, params) {
    return new Promise((resolve, reject) => {
      RetailerService.uploadRetailerDocument(params)
        .then(response => {
          commit('SET_DOCUMENTS', response.data)
          const notifications = {
            type: 'success',
            message: t('store.shared.added', {
              element: t('store.admin.retailer.retailer_document')
            })
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          resolve(response.data)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  removeRetailerDocument({ commit, dispatch }, params) {
    return new Promise((resolve, reject) => {
      RetailerService.removeRetailerDocument(params)
        .then(response => {
          commit('SET_DOCUMENTS', response.data)
          const notifications = {
            type: 'success',
            message: t('store.shared.deleted', {
              element: t('store.admin.retailer.retailer_document')
            })
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          resolve(response.data)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: t('store.shared.delete_error', {
              element: t('store.admin.retailer.retailer_document')
            })
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  downloadRetailerDocument({ commit, dispatch }, params) {
    return new Promise((resolve, reject) => {
      RetailerService.downloadRetailerDocument(params)
        .then(response => {
          let selectedDocument = state.documents.find(document => {
            return document.id === params.id
          })

          let fileURL = window.URL.createObjectURL(new Blob([response]))
          let fileLink = document.createElement('a')

          fileLink.href = fileURL
          fileLink.setAttribute('download', selectedDocument.path)
          document.body.appendChild(fileLink)

          fileLink.click()
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: t('store.admin.retailer.retailer_document_download_error')
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  toggleRetailersCurrentAccounts() {
    eventEmitter.emit('toggle-export-retailers-current-accounts')
  },
  exportRetailersCurrentAccounts({ dispatch }, params) {
    let newQuery = build_query(params.query, allowedFilters)
    newQuery.date = params.date

    return new Promise(reject => {
      RetailerService.exportRetailersCurrentAccounts(newQuery)
        .then(() => {
          const notification = {
            type: 'info',
            message: t('store.shared.exporting', {
              element: t('store.admin.retailer.retailer_current_accounts')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.exporting_error', {
                element: t('store.admin.retailer.retailer_current_accounts')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  toggleForwardSalesChannelOverriddenStatus({ dispatch }, retailerId) {
    return new Promise((resolve, reject) => {
      RetailerService.toggleForwardSalesChannelOverriddenStatus(retailerId)
        .then(response => {
          const notifications = {
            type: 'success',
            message: t('store.admin.retailer.toggle_forward_sales_channel_overridden_status_success')
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: t('store.admin.retailer.toggle_forward_sales_channel_overridden_status_error')
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  toggleFarmerPaidOrderCheckOverriddenStatus({ dispatch }, retailerId) {
    return new Promise((resolve, reject) => {
      RetailerService.toggleFarmerPaidOrderCheckOverriddenStatus(retailerId)
        .then(response => {
          const notifications = {
            type: 'success',
            message: t('store.admin.retailer.toggle_farmer_paid_order_check_overridden_status_success')
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: t('store.admin.retailer.toggle_farmer_paid_order_check_overridden_status_error')
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  toggleShortSaleOverriddenStatus({ dispatch, commit }, retailerId) {
    return new Promise((resolve, reject) => {
      RetailerService.toggleShortSaleOverriddenStatus(retailerId)
        .then(response => {
          commit('SET_RETAILER', response.data)
          const notifications = {
            type: 'success',
            message: t('store.admin.retailer.toggle_short_sale_overridden_status')
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  toggleSalesChannelStatuses({ dispatch, commit }, retailerId) {
    return new Promise((resolve, reject) => {
      RetailerService.toggleSalesChannelStatuses(retailerId)
        .then(response => {
          commit('SET_RETAILER', response.data)
          const notifications = {
            type: 'success',
            message: t('store.admin.retailer.toggle_forward_sales_channel_overridden_status_success')
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  togglePaidPromissoryNotesShipping({ dispatch, commit }, retailerId) {
    return new Promise((resolve, reject) => {
      RetailerService.togglePaidPromissoryNotesShipping(retailerId)
        .then(response => {
          const notifications = {
            type: 'success',
            message: t('store.admin.retailer.toggle_paid_promissory_notes_shipping')
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getRetailerContacts({ commit, dispatch }, retailerId) {
    return new Promise((resolve, reject) => {
      RetailerService.getRetailerContacts(retailerId)
        .then(response => {
          commit('SET_RETAILER_CONTACTS', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  }
}
